import Vue from "vue";
import { $functions, $firestore } from "@/firebase";
import _ from "lodash";
import { doc, getDoc } from "firebase/firestore";

const defaultState = () => {
  return {
    observed: {}
  };
};

const state = defaultState();

const getters = {
  promotion: state => promotionId => {
    return state.observed[promotionId];
  }
};

const actions = {
  observePromotion: ({ commit, dispatch }, { promotionId }) => {
    const ref = doc($firestore, `promotions`, promotionId);
    return dispatch(
      "observe",
      {
        key: `promotions/${promotionId}`,
        ref,
        onSnapshot: promotion => {
          if (promotion.metadata.fromCache) return;
          if (!promotion.exists()) {
            dispatch("unobservePromotion", { promotionId: promotionId });
            return;
          }
          commit("setPromotion", {
            promotionId: promotionId,
            promotionData: promotion.data()
          });
        },
        onError: () => {
          dispatch("unobservePromotion", { promotionId: promotionId });
        }
      },
      { root: true }
    );
  },
  unobservePromotion: ({ commit, dispatch }, { promotionId }) => {
    return dispatch(`unobserve`, `promotions/${promotionId}`, {
      root: true
    }).then(remove => {
      if (remove) {
        commit("unsetPromotion", { promotionId: promotionId });
      }
    });
  },
  reset: async ({ dispatch, commit }) => {
    try {
      await dispatch(
        "unobserveAll",
        { containing: "promotions" },
        { root: true }
      );
      commit("resetState");
    } catch (error) {
      console.error(error);
    }
  },
  /* Marketing description for a valid coupon, or "" if it is invalid/used.
  Cannot distinguish "invalid" from "valid but undescribed" – use
  'validateCoupon' where that difference matters. */
  getDetails: async ({ dispatch }, { couponId }) => {
    const { description } = await dispatch("validateCoupon", { couponId });
    return description;
  },
  /* Like 'getDetails', but distinguishes an invalid/used coupon from a valid
  one with no marketing description. */
  // eslint-disable-next-line no-empty-pattern
  validateCoupon: async ({}, { couponId }) => {
    try {
      // Get coupon
      const couponRef = doc($firestore, `coupons/${couponId}`);
      const coupon = await getDoc(couponRef);
      if (!coupon.exists()) return { isValid: false, description: "" };
      // Check coupon usage & expiry
      let { useLimit, useCount, dateExpires, promotionRef } = coupon.data();
      if (useLimit && useCount >= useLimit)
        return { isValid: false, description: "" };
      if (dateExpires && dateExpires.toDate() < new Date())
        return { isValid: false, description: "" };
      if (!promotionRef) return { isValid: false, description: "" };
      // Get promotion
      const promotion = await getDoc(promotionRef);
      if (!promotion.exists()) return { isValid: false, description: "" };
      // Check promotion usage & expiry
      ({ useLimit, useCount, dateExpires } = promotion.data());
      if (useLimit && useCount >= useLimit)
        return { isValid: false, description: "" };
      if (dateExpires && dateExpires.toDate() < new Date())
        return { isValid: false, description: "" };
      return { isValid: true, description: promotion.data().description || "" };
    } catch (error) {
      // Invalid coupon or no linked promotion
      return { isValid: false, description: "" };
    }
  },
  // eslint-disable-next-line no-empty-pattern
  redeemCoupon: ({}, { couponId }) => {
    return $functions().httpsCallable("coupon-redeem")({ couponId });
  }
};

const mutations = {
  setPromotion: (state, { promotionId, promotionData }) => {
    Vue.set(
      state.observed,
      promotionId,
      _.merge(
        {
          _id: promotionId
        },
        promotionData
      )
    );
  },
  unsetPromotion: (state, { promotionId }) => {
    if (state.observed[promotionId]) {
      Vue.delete(state.observed, promotionId);
    }
  },
  resetState: state => {
    Object.assign(state, defaultState());
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
